import React, { useEffect, useState } from 'react';
import { Link, useLocation, NavLink } from 'react-router-dom';
import { Menu, X, MapPin, Phone, Mail, Instagram } from 'lucide-react';
export function Layout({
  children
}: {
  children: React.ReactNode;
}) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 40);
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    setIsMobileMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);
  // Header styling logic
  // - On home: transparent over hero, solid black after scroll
  // - On other pages: always solid black
  const useTransparentHeader = isHome && !isScrolled;
  const headerBg = useTransparentHeader ? 'bg-transparent' : 'bg-reach-black';
  const navItems = [{
    to: '/',
    label: 'Home'
  }, {
    to: '/programs',
    label: 'Programs'
  }, {
    to: '/summer-2026',
    label: 'Summer 2026'
  }, {
    to: '/about',
    label: 'About'
  }, {
    to: '/pricing',
    label: 'Pricing'
  }, {
    to: '/contact',
    label: 'Contact'
  }];
  return <div className="min-h-screen flex flex-col bg-reach-white" data-id="element-17">
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${headerBg} ${isScrolled ? 'py-2 border-b border-white/10' : 'py-2 md:py-3'}`} data-id="element-18">
        <div className="max-w-container mx-auto px-4 md:px-8 flex items-center justify-between" data-id="element-19">
          {/* Logo — white horizontal banner on dark header */}
          <Link to="/" className="flex items-center z-50 shrink-0" aria-label="Reach Performance Home" data-id="element-20">
            <img src="https://cdn.magicpatterns.com/uploads/esRWbJLRpNThsrPH4a2raJ/Reach-Banner-Wht.png" alt="Reach Performance" className="h-24 md:h-32 w-auto object-contain" data-id="element-21" />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-7" data-id="element-22">
            {navItems.map(item => <NavLink key={item.to} to={item.to} end={item.to === '/'} className={({
            isActive
          }) => `font-inter text-sm font-medium uppercase tracking-wider transition-colors text-white hover:text-reach-red ${isActive ? 'text-reach-red' : ''}`} data-id="element-23">
                {item.label}
              </NavLink>)}
          </nav>

          {/* Right side actions */}
          <div className="hidden lg:flex items-center gap-5" data-id="element-24">
            <Link to="/book" className="font-inter text-sm font-medium uppercase tracking-wider text-white hover:text-reach-red transition-colors" data-id="element-25">
              Book Consultation
            </Link>
            <Link to="/pricing" className="btn-primary !min-h-[40px] !px-5 !text-xs" data-id="element-26">
              Enroll Now
            </Link>
          </div>

          {/* Mobile Hamburger */}
          <button className="lg:hidden z-50 p-2 text-white" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'} aria-expanded={isMobileMenuOpen} data-id="element-27">
            {isMobileMenuOpen ? <X size={28} data-id="element-28" /> : <Menu size={28} data-id="element-29" />}
          </button>
        </div>

        {/* Mobile Nav Overlay */}
        <div className={`fixed inset-0 bg-reach-black z-40 flex flex-col pt-28 px-6 transition-transform duration-300 ease-in-out lg:hidden ${isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`} data-id="element-30">
          <nav className="flex flex-col gap-1" data-id="element-31">
            {navItems.map(item => <NavLink key={item.to} to={item.to} end={item.to === '/'} className={({
            isActive
          }) => `font-bebas text-white text-4xl tracking-wide border-b border-white/10 py-4 ${isActive ? 'text-reach-red' : ''}`} data-id="element-32">
                {item.label}
              </NavLink>)}
          </nav>
          <div className="mt-auto pb-12 flex flex-col gap-3" data-id="element-33">
            <Link to="/pricing" className="btn-primary w-full" data-id="element-34">
              Enroll Now
            </Link>
            <Link to="/book" className="btn-secondary-dark w-full" data-id="element-35">
              Book Free Consultation
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content — home is full-bleed (hero handles top space); other pages get top padding */}
      <main className={`flex-grow ${isHome ? '' : 'pt-32 md:pt-40'}`} data-id="element-36">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-reach-black text-white pt-16 pb-8" data-id="element-37">
        <div className="max-w-container mx-auto px-4 md:px-8" data-id="element-38">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12" data-id="element-39">
            {/* Column 1: Brand wordmark + tagline */}
            <div data-id="element-40">
              <img src="https://cdn.magicpatterns.com/uploads/esRWbJLRpNThsrPH4a2raJ/Reach-Banner-Wht.png" alt="Reach Performance" className="w-[200px] h-auto object-contain mb-5" data-id="element-41" />
              <p className="font-bebas text-reach-red text-sm tracking-[0.2em] mb-5" data-id="element-42">
                STABILITY · STRENGTH · POWER
              </p>
              <p className="font-inter text-sm text-gray-400 leading-relaxed" data-id="element-43">
                Athletic performance training for athletes ages 8 to 18 in North
                Texas.
              </p>
            </div>

            {/* Column 2: Programs */}
            <div data-id="element-44">
              <h3 className="font-bebas text-xl tracking-widest text-reach-red mb-5" data-id="element-45">
                PROGRAMS
              </h3>
              <ul className="space-y-3 font-inter text-sm text-gray-300" data-id="element-46">
                <li data-id="element-47">
                  <Link to="/programs" className="hover:text-white transition-colors" data-id="element-48">
                    Advanced Performance
                  </Link>
                </li>
                <li data-id="element-49">
                  <a href="/#pro-athletes" className="hover:text-white transition-colors" data-id="element-50">
                    Pro Athletes
                  </a>
                </li>
                <li data-id="element-51">
                  <Link to="/programs" className="hover:text-white transition-colors" data-id="element-52">
                    Performance Prep
                  </Link>
                </li>
                <li data-id="element-53">
                  <Link to="/programs" className="hover:text-white transition-colors" data-id="element-54">
                    Foundations
                  </Link>
                </li>
                <li data-id="element-55">
                  <Link to="/book" className="hover:text-white transition-colors" data-id="element-56">
                    Free Consultation
                  </Link>
                </li>
              </ul>
            </div>

            {/* Column 3: Resources */}
            <div data-id="element-57">
              <h3 className="font-bebas text-xl tracking-widest text-reach-red mb-5" data-id="element-58">
                RESOURCES
              </h3>
              <ul className="space-y-3 font-inter text-sm text-gray-300" data-id="element-59">
                <li data-id="element-60">
                  <Link to="/about" className="hover:text-white transition-colors" data-id="element-61">
                    About
                  </Link>
                </li>
                <li data-id="element-62">
                  <Link to="/summer-2026" className="hover:text-white transition-colors" data-id="element-63">
                    Summer 2026
                  </Link>
                </li>
                <li data-id="element-64">
                  <Link to="/pricing" className="hover:text-white transition-colors" data-id="element-65">
                    Pricing
                  </Link>
                </li>
                <li data-id="element-66">
                  <Link to="/contact" className="hover:text-white transition-colors" data-id="element-67">
                    Contact
                  </Link>
                </li>
                <li data-id="element-68">
                  <Link to="/book" className="hover:text-white transition-colors" data-id="element-69">
                    Book Consultation
                  </Link>
                </li>
              </ul>
            </div>

            {/* Column 4: Get in Touch */}
            <div data-id="element-70">
              <h3 className="font-bebas text-xl tracking-widest text-reach-red mb-5" data-id="element-71">
                GET IN TOUCH
              </h3>
              <ul className="space-y-4 font-inter text-sm text-gray-300" data-id="element-72">
                <li className="flex items-start gap-3" data-id="element-73">
                  <MapPin size={18} className="shrink-0 mt-0.5 text-reach-red" data-id="element-74" />
                  <a href="https://maps.google.com/?q=6051+Alma+Rd,+McKinney,+TX+75070" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors leading-relaxed" data-id="element-75">
                    INPOWERiQ Performance Center
                    <br data-id="element-76" />
                    6051 Alma Rd
                    <br data-id="element-77" />
                    McKinney, TX 75070
                  </a>
                </li>
                <li className="flex items-center gap-3" data-id="element-78">
                  <Phone size={18} className="shrink-0 text-reach-red" data-id="element-79" />
                  <a href="tel:7089804666" className="hover:text-white transition-colors" data-id="element-80">
                    (708) 980-4666
                  </a>
                </li>
                <li className="flex items-center gap-3" data-id="element-81">
                  <Mail size={18} className="shrink-0 text-reach-red" data-id="element-82" />
                  <a href="mailto:Fernando@reachperformancehq.com" className="hover:text-white transition-colors break-all" data-id="element-83">
                    Fernando@reachperformancehq.com
                  </a>
                </li>
                <li className="flex items-center gap-3" data-id="element-84">
                  <Instagram size={18} className="shrink-0 text-reach-red" data-id="element-85" />
                  <a href="https://instagram.com/reachperformancehq" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors" data-id="element-86">
                    @reachperformancehq
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="border-t border-white/10 pt-6 flex flex-col md:flex-row justify-between items-center gap-3" data-id="element-87">
            <p className="font-inter text-xs text-gray-500" data-id="element-88">
              © 2026 Reach Performance LLC. All rights reserved.
            </p>
            <p className="font-bebas text-xs tracking-[0.25em] text-gray-500" data-id="element-89">
              STABILITY · STRENGTH · POWER
            </p>
          </div>
        </div>
      </footer>
    </div>;
}